import React from 'react';
import {
  Crown, Sparkles, Receipt, ArrowUpCircle, AlertCircle, CheckCircle2, Megaphone,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

const PLAN_DETAILS = {
  pro: {
    label: 'Pro',
    price: '£99.99/mo',
    blurb: 'Full drill-down on every property — AI score, AVM, comparables, yield and planning.',
    icon: Crown,
    accent: 'emerald',
  },
  basic: {
    label: 'Basic',
    price: '£49.99/mo',
    blurb: 'Search, alerts and summary property views. Upgrade to Pro for full drill-down.',
    icon: Sparkles,
    accent: 'blue',
  },
  ad: {
    label: 'Advertise',
    price: '£14.99/mo',
    blurb: 'Your banner in the PropScope ad bar on every page.',
    icon: Megaphone,
    accent: 'amber',
  },
  none: {
    label: 'No active plan',
    price: '—',
    blurb: 'Choose Basic or Pro for platform access, or Advertise for the ad bar.',
    icon: AlertCircle,
    accent: 'slate',
  },
  admin: {
    label: 'Admin',
    price: 'Internal',
    blurb: 'Internal staff account. All features available without billing.',
    icon: Crown,
    accent: 'rose',
  },
};

const STATUS_LABEL = {
  trial:     { label: 'On free trial', tone: 'amber' },
  active:    { label: 'Active', tone: 'emerald' },
  past_due:  { label: 'Payment failed — please update', tone: 'rose' },
  cancelled: { label: 'Cancelled', tone: 'slate' },
  none:      { label: 'No active plan', tone: 'slate' },
};

const ACCENT_CLASS = {
  emerald: 'border-emerald-500/30 bg-emerald-500/5',
  amber:   'border-amber-500/30 bg-amber-500/5',
  blue:    'border-blue-500/30 bg-blue-500/5',
  purple:  'border-purple-500/30 bg-purple-500/5',
  rose:    'border-rose-500/30 bg-rose-500/5',
  slate:   'border-slate-700 bg-slate-800/40',
};

const ICON_CLASS = {
  emerald: 'text-emerald-400',
  amber:   'text-amber-400',
  blue:    'text-blue-400',
  purple:  'text-purple-400',
  rose:    'text-rose-400',
  slate:   'text-slate-400',
};

const STATUS_COLOUR = {
  amber:   'text-amber-300 bg-amber-500/10 border-amber-500/30',
  emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  rose:    'text-rose-300 bg-rose-500/10 border-rose-500/30',
  slate:   'text-slate-300 bg-slate-800 border-slate-700',
};


export default function PlanCard({ user }) {
  if (!user) return null;

  const access = user.data_access_level || 'none';
  const status = user.subscription_status || 'none';
  const platformPlan = PLAN_DETAILS[access] || PLAN_DETAILS.none;
  const showAd = user.is_advertiser;

  if (user.lifetime_free) {
    return <LifetimeFreeCard planName={platformPlan.label} />;
  }

  const canUsePortal = !user.lifetime_free;

  const openPortal = async () => {
    try {
      const res = await api.post('/api/billing/portal');
      const url = res.data?.url || res.data?.portal_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Stripe did not return a portal URL.');
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error('Contact support about billing.');
      } else {
        toast.error("Couldn't open the Stripe billing portal. Try again or contact support.");
      }
    }
  };

  const goUpgrade = () => {
    toast.success("Opening Stripe — you'll be able to switch plans from there.");
    openPortal();
  };

  const statusInfo = STATUS_LABEL[status] || STATUS_LABEL.none;
  const trialEnd = user.trial_end_at ? new Date(user.trial_end_at) : null;
  const trialActive = !!(trialEnd && trialEnd > new Date());

  return (
    <div className="space-y-4">
      {access !== 'none' && (
        <PlanTile
          details={platformPlan}
          statusInfo={statusInfo}
          trialActive={trialActive}
          trialEnd={trialEnd}
          onPortal={canUsePortal ? openPortal : null}
          onUpgrade={access === 'basic' ? goUpgrade : null}
          upgradeLabel="Upgrade to Pro (£99.99/mo)"
        />
      )}

      {showAd && (
        <PlanTile
          details={PLAN_DETAILS.ad}
          statusInfo={{ label: 'Ad bar active', tone: 'amber' }}
          onPortal={canUsePortal ? openPortal : null}
        />
      )}

      {access === 'none' && !showAd && (
        <PlanTile
          details={PLAN_DETAILS.none}
          statusInfo={statusInfo}
          onPortal={canUsePortal ? openPortal : null}
        />
      )}
    </div>
  );
}


function PlanTile({ details, statusInfo, trialActive, trialEnd, onPortal, onUpgrade, upgradeLabel }) {
  const Icon = details.icon || Sparkles;
  const accent = details.accent || 'slate';

  return (
    <div className={`rounded-2xl border p-5 ${ACCENT_CLASS[accent]}`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl ${ACCENT_CLASS[accent]} border flex items-center justify-center flex-shrink-0`}>
          <Icon size={20} className={ICON_CLASS[accent]} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-lg font-semibold text-white">{details.label}</h2>
            <span className="text-slate-400 text-sm">{details.price}</span>
            {statusInfo && (
              <span className={`ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border ${STATUS_COLOUR[statusInfo.tone]}`}>
                {statusInfo.tone === 'emerald' && <CheckCircle2 size={11} />}
                {statusInfo.label}
              </span>
            )}
          </div>
          <p className="text-slate-400 text-sm mt-1.5">{details.blurb}</p>
          {onPortal && (
            <p className="text-slate-500 text-xs mt-2">
              Opens Stripe&apos;s customer portal — view invoices, switch plans, or cancel at end of period.
            </p>
          )}
          {trialActive && (
            <p className="text-amber-300 text-xs mt-2">
              Trial ends <strong>{trialEnd.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {onUpgrade && (
              <button onClick={onUpgrade}
                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold rounded-lg px-3 py-2">
                <ArrowUpCircle size={14} /> {upgradeLabel}
              </button>
            )}
            {onPortal ? (
              <button onClick={onPortal}
                className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg px-3 py-2">
                <Receipt size={14} /> Invoices, plan &amp; cancel
              </button>
            ) : (
              <Link to="/choose-plan"
                className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg px-3 py-2">
                <Receipt size={14} /> Choose a plan
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function LifetimeFreeCard({ planName }) {
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-center justify-center flex-shrink-0">
          <Crown size={20} className="text-amber-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <h2 className="text-lg font-semibold text-white">{planName}</h2>
            <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border bg-amber-500/10 text-amber-300 border-amber-500/30">
              <Sparkles size={11} /> Lifetime free
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1.5">
            Your account will never be charged.
          </p>
        </div>
      </div>
    </div>
  );
}
